import { useId, useState } from 'react';
import { usePublishFloatingHeight } from '@/hooks/usePublishFloatingHeight';
import { useTranslation } from 'react-i18next';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { isIOSSafari, isStandalone } from '@/utils/platform';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';

/**
 * Slim PWA Install Bar
 *
 * Mobile/tablet-only (<1024px), sticky bottom bar that sits above the
 * mobile `BottomNav`. Unlike a Chromium-only modal sheet, this component
 * ALSO supports iOS Safari, which never fires `beforeinstallprompt` and
 * therefore needs a manual "Add to Home Screen" hint instead of a native
 * prompt.
 */
export const InstallBar = () => {
  const { t } = useTranslation('common');
  const isMobileOrTablet = useMediaQuery('(max-width: 1023px)');
  const { isInstalled, canShowPrompt, isDismissed, isEngaged, promptInstall, dismissPrompt } = useInstallPrompt();
  const [showIOSHint, setShowIOSHint] = useState(false);
  const iosHintId = useId();
  const [barEl, setBarEl] = useState<HTMLDivElement | null>(null);
  // Publish own height (+ the 8px mb-2 gap) so the banner / FAB / checklist stack above the bar (index.css).
  usePublishFloatingHeight(barEl, '--floating-install-h', 8);

  // iOS Safari never fires `beforeinstallprompt`, so `canShowPrompt` (which
  // depends on a captured deferred prompt) is always false there. Gate the
  // iOS fallback on platform detection + the same dismiss snooze AND the
  // same anti-annoyance engagement heuristic the Android path enforces via
  // `canShowPrompt`, so a first-time iOS visitor doesn't see the bar instantly.
  const iosEligible = !isDismissed && isEngaged && isIOSSafari() && !isStandalone();

  const shouldRender = isMobileOrTablet && !isInstalled && (canShowPrompt || iosEligible);

  if (!shouldRender) return null;

  // When `canShowPrompt` is false but the bar is still rendering, we're on
  // the iOS fallback path (no native prompt to trigger).
  const isIOSPath = !canShowPrompt;

  const handleInstallClick = (): void => {
    if (canShowPrompt) {
      void promptInstall();
      return;
    }
    // iOS path: there's no native prompt, so reveal the manual instructions.
    setShowIOSHint((prev) => !prev);
  };

  return (
    <div
      ref={setBarEl}
      role="region"
      aria-label={t('install_banner_title')}
      className="lg:hidden fixed left-0 right-0 bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom))] z-40"
    >
      <div className="mx-2 mb-2 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
        <div className="flex items-center gap-3 px-3 py-2.5">
          <img
            src="/pwa-192x192.png"
            alt=""
            aria-hidden="true"
            className="w-8 h-8 rounded-lg shadow-sm flex-shrink-0"
          />

          <p className="flex-1 min-w-0 text-sm font-medium text-gray-900 truncate">
            {t('install_banner_title')}
          </p>

          <button
            type="button"
            onClick={handleInstallClick}
            {...(isIOSPath ? { 'aria-expanded': showIOSHint, 'aria-controls': iosHintId } : {})}
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-[#ff5757] hover:bg-[#e04b4b] active:bg-[#c94141] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#ff5757]"
          >
            <Icon name={ICONS.DOWNLOAD} size="xs" color="text-white" ariaHidden />
            {isIOSPath
              ? t('install_app_ios_badge', { defaultValue: 'Manual install' })
              : t('install_banner_install')}
          </button>

          <button
            type="button"
            onClick={dismissPrompt}
            aria-label={t('close_install_banner', { defaultValue: 'Close install banner' })}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
          >
            <Icon name={ICONS.CLOSE} size="sm" ariaHidden />
          </button>
        </div>

        {showIOSHint && (
          <div
            id={iosHintId}
            className="flex items-center gap-2 px-3 py-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-600"
          >
            <Icon name={ICONS.SHARE} size="xs" color="text-gray-500" ariaHidden />
            <span>
              {t('install_app_ios_instructions', {
                defaultValue: 'Tap the Share icon, then "Add to Home Screen".',
              })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
